import { useRef, useState } from 'react';
import { Camera, Upload, Loader2, X, CheckCircle2, AlertTriangle, FileImage } from 'lucide-react';
import { compressImage, formatBytes } from '@/utils/imageCompression';
import { extractFromDocument, type ExtractedPatientData } from '@/services/patients/documentOcr';

interface Props {
  /**
   * Called after the file is compressed AND OCR has returned.
   * `compressedFile` is what should be uploaded to Storage.
   * `extracted` contains the fields parsed from the document (may be mostly empty).
   */
  onExtracted: (compressedFile: File, extracted: ExtractedPatientData) => void;
  /** Called when the user clears/removes the captured file. */
  onClear?: () => void;
  /** Disable the inputs (e.g. while the form is saving). */
  disabled?: boolean;
}

type Status = 'idle' | 'compressing' | 'ocr' | 'done' | 'error';

export const DocumentCapture = ({ onExtracted, onClear, disabled }: Props) => {
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [status, setStatus] = useState<Status>('idle');
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [compressedFile, setCompressedFile] = useState<File | null>(null);
  const [extracted, setExtracted] = useState<ExtractedPatientData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setError(null);
    setStatus('compressing');

    try {
      // 1) Compress (resize + re-encode as JPEG ~85%)
      const compressed = await compressImage(file, { maxDim: 1600, quality: 0.85 });
      setCompressedFile(compressed);

      // Preview from the compressed version — what will actually be uploaded
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(compressed));

      // 2) OCR via Gemini (3 tokens)
      setStatus('ocr');
      const data = await extractFromDocument(compressed);
      setExtracted(data);
      setStatus('done');

      onExtracted(compressed, data);
    } catch (err: unknown) {
      console.error('Document capture error:', err);
      const msg = err instanceof Error ? err.message : 'Erro ao processar documento.';
      setError(msg);
      setStatus('error');
    }
  };

  const pickCamera = () => cameraInputRef.current?.click();
  const pickFile = () => fileInputRef.current?.click();

  const onFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so selecting the same file again still triggers change
    e.target.value = '';
  };

  const clear = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setCompressedFile(null);
    setExtracted(null);
    setStatus('idle');
    setError(null);
    onClear?.();
  };

  // Count how many fields were actually extracted (for UX feedback)
  const extractedCount = extracted
    ? [extracted.name, extracted.cpf, extracted.birthDate, extracted.rg, extracted.gender].filter(Boolean).length
    : 0;

  return (
    <div className="rounded-lg border border-dashed border-primary/30 bg-primary/5 p-4">
      <div className="flex items-center gap-2 mb-3">
        <FileImage size={16} className="text-primary" />
        <h4 className="text-sm font-semibold text-on-surface">Documento do paciente (opcional)</h4>
      </div>
      <p className="text-xs text-on-surface-variant mb-3">
        Tire uma foto ou faça upload do RG/CNH. Os campos serão preenchidos automaticamente.
      </p>

      {/* Hidden native inputs */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={onFileInput}
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFileInput}
      />

      {/* Idle / initial state */}
      {status === 'idle' && !previewUrl && (
        <div className="grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={pickCamera}
            disabled={disabled}
            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-primary text-on-primary text-sm font-medium hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Camera size={16} />
            Tirar Foto
          </button>
          <button
            type="button"
            onClick={pickFile}
            disabled={disabled}
            className="flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-surface-container text-on-surface text-sm font-medium border border-outline-variant/30 hover:bg-surface-container-high disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            <Upload size={16} />
            Upload
          </button>
        </div>
      )}

      {/* Processing / done / error states */}
      {previewUrl && (
        <div className="space-y-3">
          <div className="relative rounded-lg overflow-hidden border border-outline-variant/20 bg-surface-container">
            <img src={previewUrl} alt="Documento" className="w-full max-h-48 object-contain" />
            <button
              type="button"
              onClick={clear}
              disabled={disabled || status === 'ocr'}
              className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 text-white hover:bg-black/80 flex items-center justify-center disabled:opacity-50"
              title="Remover"
            >
              <X size={14} />
            </button>
          </div>

          {/* Status line */}
          {status === 'compressing' && (
            <div className="flex items-center gap-2 text-xs text-on-surface-variant">
              <Loader2 size={14} className="animate-spin" />
              Comprimindo imagem…
            </div>
          )}

          {status === 'ocr' && (
            <div className="flex items-center gap-2 text-xs text-on-surface-variant">
              <Loader2 size={14} className="animate-spin" />
              Extraindo dados do documento… (consome 3 tokens)
            </div>
          )}

          {status === 'done' && extracted && (
            <div className="text-xs space-y-1">
              <div className="flex items-center gap-2 text-success">
                <CheckCircle2 size={14} />
                <span className="font-medium">
                  {extractedCount > 0
                    ? `${extractedCount} campo${extractedCount === 1 ? '' : 's'} extraído${extractedCount === 1 ? '' : 's'}`
                    : 'Documento anexado (nenhum campo reconhecido)'}
                </span>
              </div>
              {compressedFile && (
                <div className="text-on-surface-variant/70">
                  Tamanho: {formatBytes(compressedFile.size)}
                </div>
              )}
            </div>
          )}

          {status === 'error' && (
            <div className="flex items-start gap-2 text-xs text-error">
              <AlertTriangle size={14} className="shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
